import React, {useCallback, useState, useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {Filter, FilterKey, Operator, SearchContext, keyToEmptyFilter, SortKey} from 'search/SearchConstants';
import ItemSearchInput, {ItemSearchInputProps} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {ElementKeyboardEvent} from 'util/Types';

import {
  StudyListingGraphQL_StudyNode_archiveData_ArchiveDataNode as ArchiveDataNode,
  StudyListingGraphQL_StudyNode_folder_FolderNode as FolderNode,
  StudyListingGraphQL_StudyNode_permissions_PermissionsNode as PermissionsNode,
  StudyListingGraphQL_StudyNode_schema_StudySchemaNode as StudySchemaNode,
} from '../../graphql-fragments/studies/__generated/StudyListingGraphQL';

export type StudySearchItemKeys = keyof StudySearchItem;
export interface StudySearchItem {
  id: StrictString;
  humanId: StrictString;
  name: StrictString;
  createdAt?: StrictString;
  modifiedAt?: StrictString;
  description?: StrictString | null;
  phase?: StrictString;
  tags?: Array<JSONScalar>;
  folder?: FolderNode;
  schema?: StudySchemaNode;
  permissions?: PermissionsNode;
  archiveData?: ArchiveDataNode;
}

export interface StudySearchResponse {
  data: Array<StudySearchItem>;
}

export interface StudySearchInputProps<TStudySearchItem = StudySearchItem> {
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  initialQuery?: string;
  onBlur?: () => void;
  onChange: (studies: Array<TStudySearchItem>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  selectedItems: Array<TStudySearchItem>;
  shouldAllowMultiple?: boolean;
  studySchemaId?: string;
  context?: SearchContext;
  /** The id passed to the input element, for associating a label with it. */
  id?: string;
  /** Specifies whether input is editable without changing UI */
  disabled?: boolean;
  /** Specifies whether the input outline renders and if is editable */
  isReadOnly?: boolean;
  /** Specifies whether chips are interactive */
  hasNonInteractiveChips?: boolean;
  hasVerticalChips?: boolean;

  // This component can optionally be controlled.
  query?: string;
  onUpdateQuery?: (query: string) => void;
}

/**
 * Input to search and select one or more studies.
 */
export default function StudySearchInput<K extends StudySearchItemKeys = StudySearchItemKeys>(
  props: StudySearchInputProps<Pick<StudySearchItem, 'id' | K>>
): JSX.Element {
  const fetchParams: ItemSearchInputProps<StudySearchItem>['fetchParams'] = useMemo(() => {
    const filters: Array<Filter> = [
      keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: ['study'],
      },
    ];
    if (props.studySchemaId) {
      filters.push({
        key: FilterKey.SCHEMA,
        operator: Operator.IS_ONE_OF,
        value: [props.studySchemaId],
      });
    }
    return {
      context: props.context,
      filters,
      offset: 0,
      limit: 10,
      sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: false}],
    };
  }, [props.context, props.studySchemaId]);

  const getOptionDatasFromResponse = useCallback(
    (resp: GlobalSearchResponse): Array<OptionData> => {
      const idsToIgnore = props.selectedItems.map((item) => item.id);
      const filteredItemIds: Array<string> = resp.ids.filter(
        (itemId: string) => !idsToIgnore.includes(itemId)
      );
      return filteredItemIds.map((itemId: string) => {
        const item = resp.studiesById[itemId];
        return {
          name: item.name,
          icon: getIconIdForSearchResult({item, type: ModelTypeConstants.STUDY}),
          value: {
            item,
            item_id: itemId,
            name: item.name,
            type: ModelTypeConstants.STUDY,
          },
        };
      });
    },
    [props.selectedItems]
  );

  return props.isReadOnly ? (
    <ChipList
      chipSize={ChipSize.SMALL}
      showMoreSize={ChipSize.SMALL}
      isVertical={props.hasVerticalChips ?? false}
      itemIds={props.selectedItems.map((item) => item.id)}
      maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
    />
  ) : (
    <ItemSearchInput<Pick<StudySearchItem, 'id' | K>, StudySearchResponse>
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      autoFocus={props.autoFocus ?? false}
      chipSize={props.chipSize}
      className={props.className}
      doSearch={searchPost}
      fetchParams={fetchParams}
      id={props.id}
      initialQuery={props.initialQuery}
      itemKey="data"
      onBlur={props.onBlur}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      placeholder={props.shouldAllowMultiple ? 'Search for studies' : 'Search for study'}
      searchURL="/1/api/search"
      selectedItems={props.selectedItems}
      shouldAllowMultiple={props.shouldAllowMultiple}
      hideSelectedResults={true}
      disabled={props.disabled}
      getOptionDatasFromResponse={getOptionDatasFromResponse}
    />
  );
}

export type StudySearchControlledItemsProps = Pick<StudySearchInputProps, 'onChange' | 'selectedItems'>;

export function useStudySearchControlledItemsProps(
  initialValue: Array<StudySearchItem>
): StudySearchControlledItemsProps {
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialValue);
  return {selectedItems: studySearchItems, onChange: setStudySearchItems};
}

export function getStudyIds(
  studySearchControlledItemsProps?: StudySearchControlledItemsProps
): Array<string> {
  if (!studySearchControlledItemsProps) {
    return [];
  }
  return studySearchControlledItemsProps?.selectedItems?.map((selectedItem) => selectedItem.id);
}
